import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Router from 'next/router';
import GTM from 'react-gtm-module';

import ReactGA from '_lib/ReactGA';
import { MainApiService } from '_services';
import { processResponse, processError } from '_helpers/api';
import {
  createSaleEventBody,
  createEcommerceItemBody,
  createEcommerceTransactionBody,
} from '_helpers/analytics';

import { PAYMENT_STATUS } from '_constants/payment';
import { STORAGE_KEYS } from '_constants/storage';
import { MODALS_KEYS } from '_constants/modals';

import {
  createPaymentBody,
  fillPaymentBodyWithSourceData,
  checkIfNumberOfPaymentAttemptsExceeded,
  checkIfCardDataNotChanged,
  increaseBillingCounter,
  createBillingHash,
} from '_helpers/payment';

import {
  storeSessionValue,
  restoreSessionValue,
  clearSessionValue,
  restoreLocalValue,
} from '_helpers/storage';

import { getBasePackage } from '_helpers/package';

import { showModal } from '_redux/modals/slice';

import {
  resetResult as resetOrderBeforePayment,
  resetForm as resetBusinessInfoForm,
  resetPackage,
  refreshPreOrderFlag,
  refreshOrderBeforePayment,
} from '_redux/order/slice';

const initForm = {
  firstName: '',
  lastName: '',
  cardNumber: null,
  cardMonth: '',
  cardYear: '',
  cvc: '',
  billingAddressCountry: 'US',
  billingAddressState: '',
  billingAddressAddress1: null,
  billingAddressCity: null,
  billingAddressZip: null,
  agreeWithTerms: false,
  campaignId: null,
  restored: false,
};

export const createPayment = createAsyncThunk(
  'payment/createPayment',
  async function (params, { dispatch, getState, rejectWithValue }) {
    const { redirectUrl } = params || {};
    const crmSessionId = restoreLocalValue(STORAGE_KEYS.crmSessionId);
    const { order, payment } = getState();

    const {
      trademarkServiceKey,
      form: { processingOption },
      data: { id: orderId, discountCouponType, tokenWithDiscount },
    } = order;

    const { form } = payment;

    const basePackage = getBasePackage(trademarkServiceKey, processingOption);
    const primaryProductCode = basePackage[0];

    const cardData = {
      cvc: form.cvc,
      cardNumber: form.cardNumber,
      cardMonth: form.cardMonth,
      cardYear: form.cardYear,
    };

    const body = createPaymentBody(form, processingOption);

    fillPaymentBodyWithSourceData(body);

    try {
      const isNumberOfPaymentAttemptsExceeded =
        checkIfNumberOfPaymentAttemptsExceeded();
      const isCardDataNotChanged = await checkIfCardDataNotChanged(cardData);

      if (isCardDataNotChanged) {
        increaseBillingCounter();
      }

      if (isNumberOfPaymentAttemptsExceeded && isCardDataNotChanged) {
        dispatch(showModal(MODALS_KEYS.paymentFailed));
        throw new Error('Transaction Declined');
      }

      if (discountCouponType && tokenWithDiscount) {
        MainApiService.token = tokenWithDiscount;
      }

      const response = await MainApiService.createPayment({
        crmSessionId,
        orderId,
        body,
      });

      MainApiService.token = null;

      const result = processResponse(response);

      const primaryProduct = result?.products?.find(
        ({ code: { code } }) => code === primaryProductCode
      );
      const isDeclined =
        primaryProduct?.paymentStatus === PAYMENT_STATUS.declined;

      if (isDeclined) {
        if (!isCardDataNotChanged) {
          createBillingHash(cardData);
          increaseBillingCounter();
        }

        dispatch(showModal(MODALS_KEYS.paymentFailed));
        dispatch(
          refreshOrderBeforePayment({ ...(order?.data || {}), declined: true })
        );

        throw new Error(
          primaryProduct?.crmErrorMessage || 'Transaction Declined'
        );
      }

      dispatch(resetOrderBeforePayment());
      dispatch(resetBusinessInfoForm());
      dispatch(resetPackage());
      dispatch(resetForm());
      dispatch(refreshPreOrderFlag(false));
      dispatch(storeOrderAfterPayment(result));
      clearSessionValue(STORAGE_KEYS.businessInfo);
      clearSessionValue(STORAGE_KEYS.packageInfo);
      clearSessionValue(STORAGE_KEYS.orderBeforePayment);
      clearSessionValue(STORAGE_KEYS.preOrderFlag);
      clearSessionValue(STORAGE_KEYS.billingInfo);
      clearSessionValue(STORAGE_KEYS.billingCounter);
      clearSessionValue(STORAGE_KEYS.billingHash);

      GTM.dataLayer(createSaleEventBody(result));

      if (result.order.paymentDetails.cardType !== 'TESTCARD') {
        result.products?.forEach((product) => {
          ReactGA.plugin.execute(
            'ecommerce',
            'addItem',
            createEcommerceItemBody({
              crmOrderId: result.order.crmOrderId,
              product,
            })
          );
        });
        ReactGA.plugin.execute(
          'ecommerce',
          'addTransaction',
          createEcommerceTransactionBody({
            crmOrderId: result.order.crmOrderId,
            total: result.order.total,
          })
        );
      }

      if (redirectUrl) {
        Router.push(redirectUrl);
      }

      return result;
    } catch (e) {
      return rejectWithValue(processError(e));
    }
  }
);

const paymentSlice = createSlice({
  name: 'payment',
  initialState: {
    form: {
      ...initForm,
    },
    data: null,
    error: null,
    loading: false,
    restored: false,
  },
  reducers: {
    refreshForm(state, action) {
      const { cardNumber, cardMonth, cardYear, cvc, ...resFields } =
        action.payload;

      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedBillingInfo =
          restoreSessionValue(STORAGE_KEYS.billingInfo) || {};

        storeSessionValue(STORAGE_KEYS.billingInfo, {
          ...storedBillingInfo,
          [trademarkServiceKey]: {
            ...(storedBillingInfo[trademarkServiceKey] || {}),
            ...resFields,
          },
        });
      }

      state.form = {
        ...state.form,
        ...action.payload,
        restored: false,
      };
    },
    restoreForm(state) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedBillingInfo =
          restoreSessionValue(STORAGE_KEYS.billingInfo) || {};

        state.form = {
          ...state.form,
          ...(storedBillingInfo[trademarkServiceKey] || initForm),
          restored: true,
        };
      }
    },
    resetForm(state) {
      state.form = { ...initForm };
    },
    storeOrderAfterPayment(_, action) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedOrderAfterPayment =
          restoreSessionValue(STORAGE_KEYS.orderAfterPayment) || {};

        storeSessionValue(STORAGE_KEYS.orderAfterPayment, {
          ...storedOrderAfterPayment,
          [trademarkServiceKey]: {
            ...(action.payload || {}),
          },
        });
      }
    },
    restoreOrderAfterPayment(state) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedOrderAfterPayment =
          restoreSessionValue(STORAGE_KEYS.orderAfterPayment) || {};
        const storedOrderAfterPaymentByActiveCode =
          storedOrderAfterPayment[trademarkServiceKey] || null;
        state.data = storedOrderAfterPaymentByActiveCode;
        state.restored = true;
      }
    },
    resetResult(state) {
      state.data = null;
    },
    resetError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPayment.pending, (state) => {
        state.error = null;
        state.data = null;
        state.loading = true;
        state.restored = false;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
  },
});

export const {
  refreshForm,
  restoreForm,
  resetForm,
  storeOrderAfterPayment,
  restoreOrderAfterPayment,
  resetResult,
  resetError,
} = paymentSlice.actions;

export default paymentSlice.reducer;
